'use client';

import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { Plus, Workflow, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import {
  PaymentFlowItemWithProcessors,
  usePaymentFlowsListWithProcessors,
} from '@/lib/api/paymentFlows';
import { useProcessorsList } from '@/lib/api/processors';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { PaymentFlowCard } from './components/payment-flow-card';
import { PaymentFlowDeleteDialog } from './components/payment-flow-delete-dialog';
import { PaymentFlowDuplicateDialog } from './components/payment-flow-duplicate-dialog';
import { ProcessorsTable } from '@/app/processors/components/ProcessorsTable';
import { MainTableState } from '@/lib/utils/table-state-utils';

/**
 * Org-level Payment Flows & Processors Page
 * Combined management for payment flows and processors
 */
const PaymentFlowsListPageInner = () => {
  const { t } = useTranslation(['paymentFlows', 'processors', 'common']);
  const { orgRoutes } = useOrgNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [flowToDelete, setFlowToDelete] = React.useState<PaymentFlowItemWithProcessors | null>(null);
  const [flowToDuplicate, setFlowToDuplicate] = React.useState<PaymentFlowItemWithProcessors | null>(null);

  // Table state for processors
  const [tableState, setTableState] = React.useState<MainTableState>({
    pagination: { pageIndex: 0, pageSize: 10 },
    sorting: [{ id: 'createdAt', desc: true }],
    columnFilters: [],
  });

  // Get active tab from URL or default to 'flows'
  const activeTab = searchParams.get('tab') || 'flows';

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const {
    data: flowsResponse,
    isLoading: isLoadingFlows,
    isError: isErrorFlows,
  } = usePaymentFlowsListWithProcessors();

  const {
    data: processorsData,
    isLoading: isLoadingProcessors,
    isFetching: isFetchingProcessors,
    isError: isErrorProcessors,
    refetch: refetchProcessors,
  } = useProcessorsList();

  const handleDeleteFlow = (flow: PaymentFlowItemWithProcessors) => {
    setFlowToDelete(flow);
  };

  const handleDuplicateFlow = (flow: PaymentFlowItemWithProcessors) => {
    setFlowToDuplicate(flow);
  };

  const paymentFlows = flowsResponse?.flows ?? [];
  const processors = processorsData ?? [];

  // Sort flows alphabetically
  const sortedFlows = [...paymentFlows].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <div className="flex flex-col space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {t('paymentFlows:title')}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('paymentFlows:combined_description')}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <TabsList>
              <TabsTrigger value="flows" className="group gap-2">
                <Workflow className="size-4" />
                {t('paymentFlows:tabs.flows')}
                {!isLoadingFlows && paymentFlows.length > 0 && (
                  <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium text-muted-foreground group-data-[state=active]:bg-primary group-data-[state=active]:text-primary-foreground">
                    {paymentFlows.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="processors" className="group gap-2">
                <ShieldCheck className="size-4" />
                {t('paymentFlows:tabs.processors')}
                {!isLoadingProcessors && processors.length > 0 && (
                  <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium text-muted-foreground group-data-[state=active]:bg-primary group-data-[state=active]:text-primary-foreground">
                    {processors.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Create button changes based on active tab */}
            {activeTab === 'flows' ? (
              <Link to={orgRoutes.PAYMENT_FLOW_CREATE()}>
                <Button>
                  <Plus className="mr-2 size-4" />
                  {t('paymentFlows:create_new')}
                </Button>
              </Link>
            ) : (
              <Link to={orgRoutes.PROCESSOR_CREATE()}>
                <Button>
                  <Plus className="mr-2 size-4" />
                  {t('processors:create_new')}
                </Button>
              </Link>
            )}
          </div>

          {/* Flows Tab Content */}
          <TabsContent value="flows" className="mt-6">
            {isLoadingFlows ? (
              <FlowsLoading />
            ) : isErrorFlows ? (
              <div className="text-destructive">{t('paymentFlows:error')}</div>
            ) : paymentFlows.length === 0 ? (
              <FlowsEmptyState />
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {sortedFlows.map((flow) => (
                  <PaymentFlowCard
                    key={flow.id}
                    flow={flow}
                    onDelete={handleDeleteFlow}
                    onDuplicate={handleDuplicateFlow}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Processors Tab Content */}
          <TabsContent value="processors" className="mt-6">
            {isLoadingProcessors ? (
              <ProcessorsLoading />
            ) : isErrorProcessors ? (
              <div className="text-destructive">{t('processors:error')}</div>
            ) : processors.length === 0 ? (
              <ProcessorsEmptyState />
            ) : (
              <ProcessorsTable
                processors={processors}
                total={processors.length}
                loading={isLoadingProcessors || isFetchingProcessors}
                onTableStateChange={setTableState}
                onRefetch={refetchProcessors}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>

      <PaymentFlowDeleteDialog
        flow={flowToDelete}
        open={!!flowToDelete}
        onOpenChange={(open) => !open && setFlowToDelete(null)}
        onClose={() => setFlowToDelete(null)}
      />

      <PaymentFlowDuplicateDialog
        flow={flowToDuplicate}
        open={!!flowToDuplicate}
        onOpenChange={(open) => !open && setFlowToDuplicate(null)}
      />
    </>
  );
};

const FlowsEmptyState = () => {
  const { t } = useTranslation(['paymentFlows', 'common']);
  const { orgRoutes } = useOrgNavigate();

  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border-2 border-dashed p-12">
      <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-primary/10">
        <Workflow className="size-8 text-primary" />
      </div>
      <h3 className="mb-2 text-lg font-semibold">
        {t('paymentFlows:empty_state.title')}
      </h3>
      <p className="mb-6 max-w-md text-center text-muted-foreground">
        {t('paymentFlows:empty_state.description')}
      </p>
      <Link to={orgRoutes.PAYMENT_FLOW_CREATE()}>
        <Button>
          <Plus className="mr-2 size-4" />
          {t('paymentFlows:create_first')}
        </Button>
      </Link>
    </div>
  );
};

const ProcessorsEmptyState = () => {
  const { t } = useTranslation(['processors', 'common']);
  const { orgRoutes } = useOrgNavigate();

  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border-2 border-dashed p-12">
      <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-primary/10">
        <ShieldCheck className="size-8 text-primary" />
      </div>
      <h3 className="mb-2 text-lg font-semibold">
        {t('processors:empty_state.title')}
      </h3>
      <p className="mb-6 max-w-md text-center text-muted-foreground">
        {t('processors:empty_state.description')}
      </p>
      <Link to={orgRoutes.PROCESSOR_CREATE()}>
        <Button>
          <Plus className="mr-2 size-4" />
          {t('processors:create_first')}
        </Button>
      </Link>
    </div>
  );
};

const FlowsLoading = () => {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-48 w-full rounded-lg" />
      ))}
    </div>
  );
};

const ProcessorsLoading = () => {
  return (
    <div className="space-y-4">
      <Skeleton className="h-12 w-full" />
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  );
};

export const PaymentFlowsListPage = withRequiredPermission(PaymentFlowsListPageInner, PERMISSIONS.PROCESSORS_VIEW);
export default PaymentFlowsListPage;
